import { useEffect, useRef, useState, useCallback } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { PitchDetector } from "pitchy";

export function usePerformanceAnalysis() {
    const [faceLandmarker, setFaceLandmarker] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cameraReady, setCameraReady] = useState(false);

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const previousLandmarksRef = useRef(null);

    // Pitch detection state
    const [pitchHz, setPitchHz] = useState(0);
    const [pitchScore, setPitchScore] = useState(0);
    const pitchScoreRef = useRef(0); // Ref to track pitch for computeMetrics
    const detectorRef = useRef(null);
    const animationFrameRef = useRef(null);
    const audioAnalyserRef = useRef(null);

    // --- CAMERA MANAGEMENT ---
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraReady(false);
    }, []);

    const initializeCamera = useCallback(async () => {
        console.log("Initializing camera in usePerformanceAnalysis...");
        setIsLoading(true);
        setError(null);
        try {
            const constraints = {
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user'
                },
                audio: true
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            console.log("Successfully got user media stream");
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            // Always set camera ready to trigger video mount
            setCameraReady(true);

            // Setup Audio for Pitch
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 2048;
            source.connect(analyser);
            audioAnalyserRef.current = analyser;

            const detector = PitchDetector.forFloat32Array(analyser.fftSize);
            detectorRef.current = detector;

            setIsLoading(false);
            return stream;
        } catch (err) {
            console.error("Camera initialization failed:", err);
            setError(`Camera error: ${err.message}. Please ensure permissions are granted.`);
            setCameraReady(false);
            setIsLoading(false);
            throw err;
        }
    }, []);

    // --- VIDEO STREAM ATTACHMENT ---
    useEffect(() => {
        if (cameraReady && videoRef.current && streamRef.current && !videoRef.current.srcObject) {
            console.log("Attaching stream to mounted video element");
            videoRef.current.srcObject = streamRef.current;
            videoRef.current.play().catch(e => console.error("Error playing video:", e));
        }
    }, [cameraReady]); // Runs when cameraReady becomes true (and video mounts)

    // --- MEDIAPIPE INITIALIZATION ---
    useEffect(() => {
        let mounted = true;

        async function init() {
            try {
                const vision = await FilesetResolver.forVisionTasks(
                    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
                );

                if (!mounted) return;

                const landmarker = await FaceLandmarker.createFromOptions(vision, {
                    baseOptions: {
                        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
                        delegate: "GPU"
                    },
                    outputFaceBlendshapes: true,
                    runningMode: "VIDEO",
                    numFaces: 1
                });

                if (mounted) {
                    setFaceLandmarker(landmarker);
                    setIsLoading(false);
                }
            } catch (err) {
                if (mounted) {
                    setError(`MediaPipe error: ${err.message}`);
                    setIsLoading(false);
                }
            }
        }

        init();
        return () => {
            mounted = false;
            stopCamera();
        };
    }, [stopCamera]);

    // --- METRICS COMPUTATION ---
    const computeMetrics = useCallback((timestamp) => {
        if (!faceLandmarker || !videoRef.current || videoRef.current.readyState < 2) return null;

        const result = faceLandmarker.detectForVideo(videoRef.current, timestamp);
        let metrics = {
            expression: 0,
            posture: 50,
            movement: 0,
            pitch: pitchScoreRef.current // Use ref to avoid stale closure
        };

        if (result.faceLandmarks && result.faceLandmarks.length > 0) {
            const landmarks = result.faceLandmarks[0];
            const blendshapes = result.faceBlendshapes?.[0]?.categories || [];

            // Expression - Enhanced with eyes and eyebrows
            // Mouth (smile, funnel, pucker)
            const smile = (blendshapes.find(b => b.categoryName === 'mouthSmileLeft')?.score || 0) +
                (blendshapes.find(b => b.categoryName === 'mouthSmileRight')?.score || 0);
            const mouthFunnel = blendshapes.find(b => b.categoryName === 'mouthFunnel')?.score || 0;
            const mouthPucker = blendshapes.find(b => b.categoryName === 'mouthPucker')?.score || 0;
            const mouthExpression = smile + mouthFunnel + mouthPucker;

            // Eyes (widening, squinting, blinking)
            const eyeWideLeft = blendshapes.find(b => b.categoryName === 'eyeWideLeft')?.score || 0;
            const eyeWideRight = blendshapes.find(b => b.categoryName === 'eyeWideRight')?.score || 0;
            const eyeSquintLeft = blendshapes.find(b => b.categoryName === 'eyeSquintLeft')?.score || 0;
            const eyeSquintRight = blendshapes.find(b => b.categoryName === 'eyeSquintRight')?.score || 0;
            const eyeBlinkLeft = blendshapes.find(b => b.categoryName === 'eyeBlinkLeft')?.score || 0;
            const eyeBlinkRight = blendshapes.find(b => b.categoryName === 'eyeBlinkRight')?.score || 0;
            const eyeExpression = eyeWideLeft + eyeWideRight + eyeSquintLeft + eyeSquintRight + eyeBlinkLeft + eyeBlinkRight;

            // Eyebrows (raising, lowering)
            const browInnerUp = blendshapes.find(b => b.categoryName === 'browInnerUp')?.score || 0;
            const browOuterUpLeft = blendshapes.find(b => b.categoryName === 'browOuterUpLeft')?.score || 0;
            const browOuterUpRight = blendshapes.find(b => b.categoryName === 'browOuterUpRight')?.score || 0;
            const browDownLeft = blendshapes.find(b => b.categoryName === 'browDownLeft')?.score || 0;
            const browDownRight = blendshapes.find(b => b.categoryName === 'browDownRight')?.score || 0;
            const browExpression = browInnerUp + browOuterUpLeft + browOuterUpRight + browDownLeft + browDownRight;

            // Combine all facial expressions with balanced sensitivity (medium difficulty)
            const combinedExpression = (mouthExpression * 0.4) + (eyeExpression * 0.35) + (browExpression * 0.25);

            // Apply threshold - only score if there's actual movement (not just neutral face)
            const rawScore = combinedExpression * 60;
            // Increased threshold to 8 to ensure static face is 0
            metrics.expression = rawScore > 8 ? Math.min(100, Math.round(rawScore)) : 0;

            // Posture (Head Roll/Tilt Alignment)
            // Calculate angle between eyes
            const eyeDX = (landmarks[263].x - landmarks[33].x) * videoRef.current.videoWidth;
            const eyeDY = (landmarks[263].y - landmarks[33].y) * videoRef.current.videoHeight;
            const faceAngleRad = Math.atan2(eyeDY, eyeDX);
            const faceAngleDeg = Math.abs(faceAngleRad * (180 / Math.PI));

            // Score: 100 at 0 degrees, drops to 0 at 45 degrees
            // "Easiest to perform is good" -> Standing straight = 100
            metrics.posture = Math.max(0, Math.round(100 - (faceAngleDeg * 2.5)));

            // Movement - Enhanced sensitivity with multiple landmark tracking
            const noseTip = landmarks[1]; // Ensure noseTip is defined
            if (previousLandmarksRef.current) {
                const prevNose = previousLandmarksRef.current[1];
                const prevLeftEye = previousLandmarksRef.current[33];
                const prevRightEye = previousLandmarksRef.current[263];

                const noseDist = Math.sqrt(
                    Math.pow((noseTip.x - prevNose.x) * videoRef.current.videoWidth, 2) +
                    Math.pow((noseTip.y - prevNose.y) * videoRef.current.videoHeight, 2)
                );
                const leftEyeDist = Math.sqrt(
                    Math.pow((landmarks[33].x - prevLeftEye.x) * videoRef.current.videoWidth, 2) +
                    Math.pow((landmarks[33].y - prevLeftEye.y) * videoRef.current.videoHeight, 2)
                );
                const rightEyeDist = Math.sqrt(
                    Math.pow((landmarks[263].x - prevRightEye.x) * videoRef.current.videoWidth, 2) +
                    Math.pow((landmarks[263].y - prevRightEye.y) * videoRef.current.videoHeight, 2)
                );

                const avgMovement = (noseDist + leftEyeDist + rightEyeDist) / 3;
                // Boosted sensitivity from 2 to 15 (User req: "more sensitive")
                metrics.movement = Math.min(100, Math.round(avgMovement * 15));
            }
            previousLandmarksRef.current = landmarks;
        }

        return { metrics, landmarks: result.faceLandmarks?.[0] || null };
    }, [faceLandmarker, pitchScore]);

    // --- PITCH DETECTION LOOP ---
    useEffect(() => {
        if (!audioAnalyserRef.current || !detectorRef.current) return;

        const buffer = new Float32Array(audioAnalyserRef.current.fftSize);
        const detect = () => {
            audioAnalyserRef.current.getFloatTimeDomainData(buffer);
            const [pitch, clarity] = detectorRef.current.findPitch(buffer, audioAnalyserRef.current.context.sampleRate);

            if (clarity > 0.9 && pitch > 0) {
                const score = Math.round((Math.min(1000, Math.max(80, pitch)) - 80) / 9.2);
                setPitchScore(score);
                setPitchHz(Math.round(pitch));
                pitchScoreRef.current = score; // Update ref for stale-free access
            }
            animationFrameRef.current = requestAnimationFrame(detect);
        };

        detect();
        return () => cancelAnimationFrame(animationFrameRef.current);
    }, [cameraReady]);

    return {
        faceLandmarker,
        isLoading,
        error,
        cameraReady,
        videoRef,
        pitchHz,
        pitchScore,
        initializeCamera,
        stopCamera,
        computeMetrics
    };
}
